'use client';

import { useEffect, useState } from 'react';
import { useCustomAddressStore, useStore } from '@libs/common-next';
import type { ProductApiResponse, TailoredApiData } from '@libs/common-types';
import { fetchMiniPdpProductBase, fetchMiniPdpProductTailored } from '../api';
import { getDeliveryMethodsList } from '../lib';
import { useMiniPdpDrawer } from './useMiniPdpDrawer';
export const useMiniPdpProduct = () => {
  const isOpen = useMiniPdpDrawer(state => state.isOpen);
  const productId = useMiniPdpDrawer(state_0 => state_0.productId);
  const {
    selectedStoreId
  } = useStore();
  const customAddress = useCustomAddressStore(state_1 => state_1.address);
  const hasAddressStateHydrated = useCustomAddressStore(state_2 => state_2._hasHydrated);
  const [base, setBase] = useState<ProductApiResponse['data'] | null>(null);
  const [tailored, setTailored] = useState<TailoredApiData | null>(null);
  const [isBaseLoading, setIsBaseLoading] = useState(false);
  const [isTailoredLoading, setIsTailoredLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  useEffect(() => {
    let stale = false;
    if (!isOpen || !productId) {
      setBase(null);
      setTailored(null);
      setIsBaseLoading(false);
      setIsTailoredLoading(false);
      setIsError(false);
      return;
    }
    setIsBaseLoading(true);
    setIsTailoredLoading(false);
    setIsError(false);
    setBase(null);
    setTailored(null);
    const load = async () => {
      const baseResponse = await fetchMiniPdpProductBase(productId);
      if (stale) return;
      if (!baseResponse) {
        setIsError(true);
        setIsBaseLoading(false);
        return;
      }
      const productData = baseResponse.data;
      const defaultVariant = productData.variants[productData.defaultSku];
      if (!defaultVariant) {
        setIsError(true);
        setIsBaseLoading(false);
        return;
      }
      setIsTailoredLoading(getDeliveryMethodsList(productData).length > 0);
      setBase(productData);
      setIsBaseLoading(false);
    };
    void load();
    return () => {
      stale = true;
    };
  }, [isOpen, productId]);
  useEffect(() => {
    let stale_0 = false;
    if (!isOpen || !productId || !base) {
      setIsTailoredLoading(false);
      return;
    }
    if (!hasAddressStateHydrated) {
      setIsTailoredLoading(true);
      return;
    }
    const deliveryMethodsList = getDeliveryMethodsList(base);
    setIsTailoredLoading(true);
    setTailored(null);
    setIsError(false);
    if (!deliveryMethodsList.length) {
      setIsTailoredLoading(false);
      return;
    }
    const load_0 = async () => {
      try {
        const tailoredData = await fetchMiniPdpProductTailored({
          deliveryMethodsList,
          storeId: selectedStoreId,
          customAddress: customAddress ?? undefined,
          variantSkus: Object.keys(base.variants)
        });
        if (stale_0) return;
        setTailored(tailoredData);
      } catch {
        if (stale_0) return;
        setIsError(true);
      } finally {
        if (!stale_0) setIsTailoredLoading(false);
      }
    };
    void load_0();
    return () => {
      stale_0 = true;
    };
  }, [base, customAddress, hasAddressStateHydrated, isOpen, productId, selectedStoreId]);
  return {
    base,
    tailored,
    isLoading: isBaseLoading || isTailoredLoading || isOpen && Boolean(base) && !hasAddressStateHydrated,
    isError
  };
};